import React from "react";
import { doesFileNameHaveImageExtension } from "core/utils/filename_checks";
import { styled } from "@mui/material/styles";

import { IconButton, Paper } from "@mui/material";
import {
  Description,
  GetApp as DownloadIcon,
  Image,
  ZoomOutMap
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import { withTransientProps } from "core/utils/transientProps";

export interface AttachmentListItemProps {
  filename: string;
  attachedByUser?: string;
  showDownload?: boolean;
  showDelete?: boolean;
  showExpand?: boolean;
  onDownloadFile?(): void;
  onExpandImage?(): void;
  onDelete?(): void;
}

export const AttachmentListItem = React.memo(function AttachmentListItem({
  filename,
  attachedByUser,
  showDownload = true,
  showDelete = true,
  showExpand = true,
  onDownloadFile,
  onExpandImage = () => {},
  onDelete
}: AttachmentListItemProps): JSX.Element {
  const isImage = doesFileNameHaveImageExtension(filename);

  return (
    <AttachmentWrapper role="listitem">
      <StartSection>
        {isImage ? <ImageIcon /> : <FileIcon />}
        <FilenameText>{filename}</FilenameText>
      </StartSection>
      <Section>
        {attachedByUser && (
          <Tooltip title={`Attached by ${attachedByUser}`}>
            <AttachedByText>{attachedByUser}</AttachedByText>
          </Tooltip>
        )}
        <ButtonContainer $deleteAllowed={Boolean(onDelete)}>
          {showExpand && isImage && onExpandImage && (
            <IconButton
              aria-label="expand image"
              title="Expand image"
              onClick={() => onExpandImage()}
              size="large"
            >
              <ZoomOutMap />
            </IconButton>
          )}
          {showDownload && onDownloadFile && (
            <IconButton
              aria-label="download attachment"
              title="Download"
              onClick={() => onDownloadFile()}
              size="large"
            >
              <DownloadIcon />
            </IconButton>
          )}
          {showDelete && onDelete && (
            <IconButton
              aria-label="delete attachment"
              title="Delete"
              onClick={() => onDelete()}
              size="large"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </ButtonContainer>
      </Section>
    </AttachmentWrapper>
  );
});

const AttachmentWrapper = styled(Paper)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  gap: 8px;
  flex-grow: 1;
  overflow: hidden;
`;

const Section = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StartSection = styled(Section)`
  overflow: hidden;
`;

const FilenameText = styled("p")`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const AttachedByText = styled("p")`
  padding: 1px 8px;
  background-color: #d9d9d9;
  border-radius: 3px;
  font-size: 11px;
  white-space: nowrap;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ButtonContainer = styled("div", withTransientProps)<{
  $deleteAllowed: boolean;
}>`
  display: flex;
  gap: 8px;
  align-items: center;
  // min-width so that attached by appears in the correct position
  min-width: ${props => (props.$deleteAllowed ? "100px" : "64px")};
  justify-content: flex-end;
`;

const ImageIcon = styled(Image)`
  color: gray;
`;

const FileIcon = styled(Description)`
  color: gray;
`;
